import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import Modal from "./Modal";

const ConfirmContext = createContext(null);

const TONES = {
  danger: "bg-red-600 hover:bg-red-700 focus-visible:ring-red-500",
  primary: "bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500",
  success: "bg-green-600 hover:bg-green-700 focus-visible:ring-green-500",
};

/**
 * Promise-based replacement for window.confirm / window.prompt.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete key?", message: "...", tone: "danger" }))) return;
 *
 * With `input` it also collects text (replaces prompt()) and resolves to the string, or null if cancelled:
 *   const reason = await confirm({ title: "Cancel add-on", input: { label: "Reason", required: true } });
 */
export function ConfirmProvider({ children }) {
  const [options, setOptions] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setInputValue(opts?.input?.defaultValue || "");
      setOptions(opts || {});
    });
  }, []);

  const close = (result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setOptions(null);
  };

  const handleCancel = () => close(options?.input ? null : false);
  const handleConfirm = () => close(options?.input ? inputValue.trim() : true);

  const inputRequired = options?.input?.required;
  const confirmDisabled = inputRequired && !inputValue.trim();

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        isOpen={!!options}
        onClose={handleCancel}
        title={options?.title || "Are you sure?"}
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-100"
            >
              {options?.cancelLabel || "Cancel"}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirmDisabled}
              className={`px-4 py-2 rounded-lg text-white font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${TONES[options?.tone] || TONES.primary}`}
            >
              {options?.confirmLabel || "Confirm"}
            </button>
          </>
        }
      >
        {options?.message && <div className="text-sm text-gray-600 whitespace-pre-line">{options.message}</div>}
        {options?.input && (
          <form
            className="mt-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!confirmDisabled) handleConfirm();
            }}
          >
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirm-dialog-input">
              {options.input.label}
              {inputRequired && <span className="text-red-500"> *</span>}
            </label>
            <textarea
              id="confirm-dialog-input"
              autoFocus
              rows={3}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={options.input.placeholder}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </form>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error("useConfirm must be used inside <ConfirmProvider>");
  }
  return confirm;
}
